import React, { useCallback, useMemo } from "react";
import { useDesktopNav } from "./navigation/DesktopNavContext";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITY, getActorRole } from "../helpers/permissions";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { IntelligenceHubDesktop } from "./intelligence/IntelligenceDesktop";
import { AccountantCsvExport } from "./invoice/AccountantCsvExport";

/**
 * IntelligenceScreen
 * Hub for reporting & analytics — mirrors CockpitHubScreen's visual language.
 * Only "Hours Reports" is active; all others show a Coming Soon state.
 */
export function IntelligenceScreen({
  onOpenHoursReports,
  onOpenPayrollReports,
  onOpenProjectAnalytics,
  onBack,
  currentUser,
}) {
  const desktopNav = useDesktopNav();
  const isDesktop = useIsDesktopViewport();
  const { can } = usePermissions();

  useHardwareBackHandler(
    useCallback(() => {
      if (typeof onBack !== "function") return false;
      onBack();
      return true;
    }, [onBack]),
  );

  const cards = useMemo(() => {
    const allCards = [
    {
      id: "hours",
      title: "Hours Reports",
      description: "View labour hours by project or date range",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
      active: true,
      accentClass: "intel-card-gold",
      onClick: onOpenHoursReports,
    },
    {
      id: "workforce",
      title: "Workforce Reports",
      description: "Headcount, utilisation and attendance summaries",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      active: false,
      accentClass: "intel-card-blue",
    },
    {
      id: "project-analytics",
      title: "Project Analytics",
      description: "Hours, labour cost, and material cost by project",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      ),
      active: true,
      accentClass: "intel-card-green",
      onClick: onOpenProjectAnalytics,
    },
    {
      id: "client-analytics",
      title: "Client Analytics",
      description: "Revenue by client, retention and engagement metrics",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      ),
      active: false,
      accentClass: "intel-card-purple",
    },
    {
  id: "payroll",
  title: "Payroll Reports",
  description:
    "Gross pay, deductions and pay period exports",
  icon: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect
        x="1"
        y="4"
        width="22"
        height="16"
        rx="2"
      />
      <line
        x1="1"
        y1="10"
        x2="23"
        y2="10"
      />
    </svg>
  ),
  active: true,
  accentClass: "intel-card-red",
  onClick: onOpenPayrollReports,
},
    ];

    const capabilityById = {
      hours: CAPABILITY.REPORT_BUILDER_VIEW,
      "project-analytics": CAPABILITY.PROJECT_ANALYTICS_VIEW,
      payroll: CAPABILITY.PAYROLL_REPORT,
    };

    return allCards.filter((card) => {
      const capability = capabilityById[card.id];
      if (!capability) {
        return true;
      }
      return can(capability);
    });
  }, [can, onOpenHoursReports, onOpenPayrollReports, onOpenProjectAnalytics]);

  if (isDesktop) {
    return (
      <IntelligenceHubDesktop
        cards={cards}
        roleLabel={getActorRole(currentUser) || ""}
      />
    );
  }

  return (
    <div className="intel-screen">
      {/* ── Header ─────────────────────────────────────── */}
      <div className="cockpit-header">
        {/* Back button */}
        {desktopNav ? (null) : (
        <button
          className="cockpit-back-btn"
          onClick={onBack}
          aria-label="Back"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: 15, height: 15 }}
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        )}
        <div
  style={{
    marginTop: 50,
  }}
>
          <div
            style={{
              textAlign: "left",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--nx-text-3)",
              marginBottom: 16,
            }}
          >
            REPORT CENTER
          </div>

          <div className="cockpit-title">
            {currentUser?.employee_name || "Crew Member"}
          </div>

          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--nx-gold-ink, #8a6d23)",
              marginTop: 4,
            }}
          >
            {getActorRole(currentUser) || "employee"}
          </div>

          <div
            style={{
              color: "var(--nx-text-muted, #5b6472)",
              marginTop: 6,
              fontSize: 14,
            }}
          >
            Let's get you some info.
          </div>
        </div>
      </div>
      <div className="acsv-entry" style={{ padding: "16px 20px 0", margin: 0 }}>
        <AccountantCsvExport buttonLabel="Accountant exports (CSV)" />
      </div>
      {/* ── Section label ───────────────────────────────── */}
      <div style={{ padding: "22px 20px 0" }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--nx-text-3)",
            borderLeft: "2px solid var(--nx-gold)",
            paddingLeft: 10,
            marginBottom: 4,
          }}
        >
          Report Modules
        </div>
      </div>

      {/* ── Cards ───────────────────────────────────────── */}
      <div className="intel-cards-list">
        {cards.map((card) => (
          <button
            key={card.id}
            className={`intel-card ${card.accentClass} ${!card.active ? "intel-card-disabled" : ""}`}
            onClick={card.active ? card.onClick : undefined}
            disabled={!card.active}
            aria-label={card.title}
          >
            <div className={`intel-card-icon-wrap intel-icon-${card.id}`}>
              {card.icon}
            </div>

            <div className="intel-card-body">
              <div className="intel-card-title">{card.title}</div>
              <div className="intel-card-desc">{card.description}</div>
            </div>

            <div className="intel-card-action">
              {card.active ? (
                <div className="intel-card-cta">
                  Open
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: 13, height: 13 }}
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              ) : (
                <div className="intel-card-soon">Soon</div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
